import type { ReactNode } from "react";
import { cx, MarkerIcon } from "../shared/markers";
import Disclaimer from "./Disclaimer";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import SisterCard from "./SisterCard";
import type { PartnerContact, PartnerMapContent } from "./types";
import styles from "./PartnerMap.module.scss";

/** One line of the contact card: what it is, and what to press or read. A line with no `href` is plain text. */
const Line = ({
	id,
	icon,
	label,
	href,
	onPress,
	children,
}: {
	id: string;
	icon: ReactNode;
	label: string;
	href?: string;
	onPress?: () => void;
	children: ReactNode;
}) => {
	const inner = (
		<>
			<i>{icon}</i>
			<span>
				<small>{label}</small>
				{children}
			</span>
		</>
	);
	return href ? (
		<a
			id={id}
			className={styles.line}
			href={href}
			target={href.startsWith("http") ? "_blank" : undefined}
			rel={href.startsWith("http") ? "noopener" : undefined}
			onClick={onPress}
		>
			{inner}
		</a>
	) : (
		<div id={id} className={styles.line}>
			{inner}
		</div>
	);
};

interface ContactTabProps {
	content: PartnerMapContent;
	contact: PartnerContact;
	onOpenPlace: (slug: string) => void;
	onContact: (channel: string) => void;
}

/**
 * The Contact tab of a restaurant or bar: where it is (with the way there), when it is open, how to reach it, one button to book and one for the menu, and the
 * sister restaurant's card.
 */
const ContactTab = ({ content, contact, onOpenPlace, onContact }: ContactTabProps) => {
	const { partner, base } = content;
	const t = strings(content.lang);
	return (
		<>
			<div className={styles.sec}>{partner.name}</div>
			<div className={styles.lines}>
				<Line
					id="partnermap-contact-address"
					icon={<UiIcon name="directions" />}
					label={t.address}
					href={base.directionsUrl}
					onPress={() => onContact("directions")}
				>
					{base.address}
					<u>{t.directions}</u>
				</Line>
				{base.hours && (
					<Line id="partnermap-contact-hours" icon={<UiIcon name="clock" />} label={t.openingHours}>
						{base.hours}
					</Line>
				)}
				{partner.tel && (
					<Line id="partnermap-contact-phone" icon={<UiIcon name="phone" />} label={t.phone} href={`tel:${partner.tel}`} onPress={() => onContact("call")}>
						{partner.telDisplay}
					</Line>
				)}
				<Line id="partnermap-contact-email" icon={<UiIcon name="mail" />} label={t.email} href={`mailto:${contact.email}`} onPress={() => onContact("email")}>
					{contact.email}
				</Line>
				<Line
					id="partnermap-contact-instagram"
					icon={<UiIcon name="instagram" />}
					label="Instagram"
					href={contact.instagram}
					onPress={() => onContact("instagram")}
				>
					{`@${contact.instagram.replace(/\/+$/, "").split("/").pop()}`}
				</Line>
			</div>
			<div className={cx(styles.acts, styles.contactActs)}>
				<a
					id="partnermap-contact-book"
					className={cx(styles.btn, styles.primary)}
					href={contact.book}
					target="_blank"
					rel="noopener"
					onClick={() => onContact("book")}
				>
					<MarkerIcon name="p_restaurant" size={15} />
					{t.bookTable}
				</a>
				<a id="partnermap-contact-menu" className={styles.btn} href={contact.menu} target="_blank" rel="noopener" onClick={() => onContact("menu")}>
					<UiIcon name="menu" />
					{t.menu}
				</a>
			</div>
			{contact.sister && (
				<>
					<div className={styles.sec}>{t.sisterSection}</div>
					<SisterCard content={content} onOpen={onOpenPlace} onContact={onContact} />
				</>
			)}
			<Disclaimer content={content} />
		</>
	);
};

export default ContactTab;
